import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import Button from 'components/inputs/Button';
import { Draggable as Drag } from 'helpers/sharedJsHelpers';
import { v2 } from 'actions';

const { modalClose } = v2.modal;

const Modal = () => {
  const modal = useSelector(state => state.modal);
  const dispatch = useDispatch();
  // When shown = null it means an animation is in progress
  const [shown, setShown] = useState(false);
  const modalEl = useRef(null);
  const modalCloseRef = useRef(null);
  const previousElement = useRef(null);
  const draggable = useRef(null);
  const prevShouldShow = useRef(modal.shouldShow);

  const fade = () => {
    const fadingIn = modal.shouldShow;

    if (fadingIn) {
      previousElement.current = document.activeElement;
      document.body.classList.add('modal-open');
    } else {
      if (draggable.current) {
        draggable.current.reset();
        draggable.current = null;
      }

      if (modal.onClose) modal.onClose();

      document.body.classList.remove('modal-open');
    }

    setShown(null);
  };

  useEffect(() => {
    if (modal.shouldShow) {
      fade();
    }
  }, []);

  useEffect(() => {
    const currentShouldShow = modal.shouldShow;
    if (prevShouldShow.current !== currentShouldShow) {
      fade();
    }
    prevShouldShow.current = currentShouldShow;
  }, [modal]);

  const onClickClose = () => {
    if (!modal.shouldShow) return;
    if (modal.closable) dispatch(modalClose());
  };

  const onKeyUp = (ev) => {
    if (ev.keyCode === 27) {
      onClickClose(ev);
    } else if (!modal.containsForm) {
      if ([32, 13].includes(ev.keyCode)) onClickClose(ev);
    }
  };

  const onFadeEnd = () => {
    const fadingIn = modal.shouldShow;

    if (fadingIn && shown !== true) {
      setShown(true);
      if (modalCloseRef.current) {
        modalCloseRef.current.focus();
      }
      if (modal.draggable) {
        draggable.current = new Drag(modalEl.current);
      }
    } else if (!fadingIn && shown !== false) {
      setShown(false);
      if (previousElement.current) {
        previousElement.current.focus();
      }
    }
  };

  const hasBorders = !!modal.title;
  const { closable } = modal;
  const closeButton = closable && modal.closeButton && modal.closeLabel;
  const closeX = closable && modal.closeX;
  const closeUnderlay = closable && modal.closeUnderlay;
  const headerClasses = classNames('modal-header', modal.headerClasses, { 'border-bottom-0': !hasBorders });
  const { actionButtons } = modal;

  let modalSize = '';
  switch (modal.size) {
    case 'xx large':
      modalSize = 'modal-xxl';
      break;
    case 'extra large':
      modalSize = 'modal-xl';
      break;
    case 'large':
      modalSize = 'modal-lg';
      break;
    case 'small':
      modalSize = 'modal-sm';
      break;
    case 'medium':
      modalSize = 'modal-md';
      break;
    default:
      break;
  }

  const shouldDisplay = shown !== false || modal.shouldShow;
  const shouldShow = shown !== false && modal.shouldShow;

  let modalBodyClassName = 'modal-body';
  if (modal.padding !== null) modalBodyClassName += ` p-${modal.padding}`;
  if (modal.paddingBottom !== null) modalBodyClassName += ` pb-${modal.paddingBottom}`;

  const roundedClassName = !modal.rounded ? 'rounded-0' : null;
  const borderClassName = modal.border !== null ? `border-${modal.border}` : null;
  const draggableStyles = modal.draggable ? { maxHeight: 'calc(100vh - 3.5rem)' } : {};
  const coverScreenStyles = modal.coverScreen ? { height: 'calc(100vh - 3.5rem)' } : {};
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <FocusTrap active={shouldDisplay} focusTrapOptions={{ escapeDeactivates: false }}>
      <div className="Modal">
        <div
          className={`modal fade ${shouldDisplay ? 'd-block' : ''} ${shouldShow ? 'show' : ''}`}
          tabIndex="0"
          role="dialog"
          ref={(el) => { modalEl.current = el; }}
          onKeyUp={onKeyUp}
          onClick={onClickClose}
          onTransitionEnd={shown === null ? onFadeEnd : () => {}}
        >
          <div
            className={`modal-dialog modal-dialog-centered ${modalSize}`}
            role="document"
            onClick={ev => ev.stopPropagation()}
          >
            <div className={`modal-content ${roundedClassName} ${borderClassName}`} style={{ ...draggableStyles, ...coverScreenStyles }}>
              {(modal.title || (closeX && !modal.closeXAbsolute)) && (
                <div className={headerClasses} style={modal.headerStyles}>
                  {modal.title && (
                    modal.titleSize === 'large' ? <h1 className="modal-title">{modal.title}</h1> : <h3 aria-level={modal.ariaLevel || 3} className="modal-title">{modal.title}</h3>
                  )}
                  {(closeX && !modal.closeXAbsolute) && (
                    <button
                      type="button"
                      className="close"
                      aria-label="Close"
                      onClick={onClickClose}
                      ref={modalCloseRef}
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  )}
                </div>
              )}
              {(closeX && modal.closeXAbsolute) && (
                <button
                  type="button"
                  className="close close-x-absolute"
                  aria-label="Close"
                  onClick={onClickClose}
                  ref={modalCloseRef}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              )}
              <div className={modalBodyClassName} style={modal.draggable ? { overflowY: 'auto' } : {}}>
                {shouldDisplay && modal.content}
              </div>
              {(!!actionButtons || !!closeButton) && (
                <div className={`modal-footer ${hasBorders ? '' : 'border-top-0'}`}>
                  {actionButtons}
                  {!!closeButton && (
                    <Button
                      key="close"
                      styleVariant="none"
                      onClick={onClickClose}
                    >
                      {modal.closeLabel}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={`modal-backdrop fade ${shouldDisplay ? '' : 'd-none'} ${shouldShow ? 'show' : ''}`}
          onKeyUp={closeUnderlay ? onKeyUp : null}
          onClick={closeUnderlay ? onClickClose : null}
        />
      </div>
    </FocusTrap>
  );
};

export default Modal;
